import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { PlayerToken, elapsedOf, fmt } from "@/pages/CoachPizarra";

const EVENT_LABEL = { gol: "⚽ Gol", gol_contra: "🥅 Gol en contra", asistencia: "🅰 Asistencia", amarilla: "🟨 Amarilla", roja: "🟥 Roja (expulsión)", cambio: "🔁 Cambio", entra: "▶ Entra", sale: "⏹ Sale", fuera_juego: "🚩 Fuera de juego", remate: "🎯 Disparo a puerta", falta_cometida: "⚠️ Falta cometida", falta_recibida: "🙌 Falta recibida", parada: "🧤 Parada", tiro_bloqueado: "🧱 Tiro bloqueado", duelo_aereo_ganado: "🦅 Duelo aéreo ganado", duelo_aereo_perdido: "💨 Duelo aéreo perdido" };

export default function PizarraLive() {
  const { userId } = useParams();
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [, setTick] = useState(0);
  const iv = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([api.get("/players"), api.get("/teams")]);
        setPlayers(p.data); setTeams(t.data);
      } catch { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`/pizarra/live/${userId}`);
        setSession(r.data?.state ? r.data : null);
      } catch { setSession(null); }
      setLoaded(true);
    };
    load();
    iv.current = setInterval(load, 4000);
    const clock = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(iv.current); clearInterval(clock); };
  }, [userId]);

  const byId = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const state = session?.state;
  const team = teams.find((t) => t.id === state?.teamId);

  if (!loaded) return <div className="text-muted-foreground text-sm">Conectando con el partido…</div>;

  if (!state || state.phase !== "board") {
    return (
      <div data-testid="live-ended">
        <div className="overline">🔴 En directo</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-2 mb-4">El partido ha terminado</h1>
        <p className="text-muted-foreground text-sm mb-6">El entrenador ha cerrado o guardado el partido. Los datos definitivos aparecerán en la jornada y en las estadísticas.</p>
        <button onClick={() => window.history.back()} data-testid="live-ended-back" className="inline-flex items-center gap-2 h-11 px-5 border border-border font-heading text-xs uppercase tracking-wider hover:border-primary">
          <ArrowLeft size={14} /> Volver
        </button>
      </div>
    );
  }

  const elapsed = elapsedOf(state.clock);
  const gf = state.events.filter((e) => e.type === "gol").length;
  const gc = state.events.filter((e) => e.type === "gol_contra").length;
  const goalsBy = {}; const yellowBy = {};
  state.events.forEach((e) => {
    if (e.type === "gol" && e.player_id) goalsBy[e.player_id] = (goalsBy[e.player_id] || 0) + 1;
    if (e.type === "amarilla" && e.player_id) yellowBy[e.player_id] = (yellowBy[e.player_id] || 0) + 1;
  });
  const badges = (pid) => {
    const b = [];
    if (goalsBy[pid]) b.push(`⚽${goalsBy[pid] > 1 ? "×" + goalsBy[pid] : ""}`);
    if (yellowBy[pid]) b.push("🟨");
    if (!state.starters.includes(pid)) b.push(<span className="text-emerald-400 font-black">▲</span>);
    return b;
  };

  return (
    <div className="max-w-3xl pb-16" data-testid="live-board">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="overline flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" /> En directo · {session.user_name || "Entrenador"} · solo lectura
          </div>
          <h1 className="font-heading font-black text-3xl tracking-tighter uppercase mt-1">
            {team?.name || "Equipo"} <span className="text-muted-foreground">vs</span> {state.opponent}
          </h1>
        </div>
        <button onClick={() => window.history.back()} data-testid="live-back-btn" className="h-10 px-4 border border-border font-heading text-xs uppercase tracking-wider inline-flex items-center gap-2 hover:border-primary">
          <ArrowLeft size={14} /> Volver
        </button>
      </div>

      <div className="border-2 border-primary p-3 mb-4 flex items-center justify-between">
        <div className="overline text-[10px]">{state.modality} · {state.homeAway === "home" ? "En casa" : "Fuera"}</div>
        <div className="font-heading font-black text-4xl tabular-nums" data-testid="live-score">{gf}-{gc}</div>
        <div className="font-heading font-black text-2xl tabular-nums flex items-center gap-2" data-testid="live-clock">
          {fmt(elapsed)}
          {state.clock.running
            ? <span className="text-[9px] font-heading uppercase text-emerald-400">En juego</span>
            : <span className="text-[9px] font-heading uppercase text-amber-400">Pausado</span>}
        </div>
      </div>

      <div className="relative w-full select-none" style={{ aspectRatio: "3/4", background: "linear-gradient(180deg,#14532d,#166534)", border: "2px solid rgba(255,255,255,0.25)" }}>
        <div className="absolute inset-3 border border-white/30 pointer-events-none" />
        <div className="absolute left-3 right-3 top-1/2 border-t border-white/30 pointer-events-none" />
        <div className="absolute left-1/2 top-1/2 w-24 h-24 -translate-x-1/2 -translate-y-1/2 border border-white/30 rounded-full pointer-events-none" />
        <div className="absolute left-1/4 right-1/4 top-3 h-14 border border-white/30 border-t-0 pointer-events-none" />
        <div className="absolute left-1/4 right-1/4 bottom-3 h-14 border border-white/30 border-b-0 pointer-events-none" />
        {state.onField.map((f) => {
          const p = byId[f.player_id];
          const isGK = f.player_id === state.gkId;
          return (
            <div key={f.player_id} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${f.x}%`, top: `${f.y}%` }}>
              <PlayerToken p={p} size={52} color={isGK ? "#059669" : "#1d4ed8"} badges={badges(f.player_id)} />
            </div>
          );
        })}
      </div>

      {state.bench.length > 0 && (
        <div className="mt-4">
          <div className="overline mb-2">Banquillo</div>
          <div className="flex gap-3 flex-wrap">
            {state.bench.map((pid) => (
              <PlayerToken key={pid} p={byId[pid]} size={44} color="#dc2626" badges={yellowBy[pid] ? ["🟨"] : []} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-5">
        <div className="overline mb-1">Registro del partido</div>
        {state.events.length === 0 && <div className="text-muted-foreground text-xs py-2">Sin eventos todavía.</div>}
        {[...state.events].reverse().map((ev) => {
          const P = (pid) => (byId[pid] ? `#${byId[pid].jersey_number || "?"} ${byId[pid].name.split(" ")[0]}` : "");
          const icon = (EVENT_LABEL[ev.type] || "•").split(" ")[0];
          const label = (EVENT_LABEL[ev.type] || ev.type).replace(/^\S+\s*/, "");
          return (
            <div key={ev.id} className="flex items-center gap-2.5 py-2 border-b border-border/30">
              <span className="w-9 h-6 flex items-center justify-center bg-primary/15 text-primary font-black text-[11px] font-mono flex-shrink-0">{ev.min}'</span>
              <span className="text-lg leading-none flex-shrink-0 w-6 text-center">{icon}</span>
              {ev.type === "cambio" ? (
                <span className="flex-1 text-[11px] font-heading font-bold uppercase leading-tight">
                  <span className="text-red-400">Sale {P(ev.out)}</span> <span className="text-muted-foreground">→</span> <span className="text-emerald-400">Entra {P(ev.in)}</span>
                </span>
              ) : (
                <span className="flex-1 text-[11px] font-heading font-bold uppercase leading-tight">
                  <span className={ev.type === "gol" ? "text-emerald-400" : ev.type === "gol_contra" || ev.type === "roja" ? "text-red-400" : ev.type === "amarilla" ? "text-amber-400" : ""}>{label}</span>
                  {ev.player_id && byId[ev.player_id] && <span className="text-foreground"> — {P(ev.player_id)}</span>}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
